import { useSettingsModal } from '@/app-shell/settings/hooks/useSettingsModal';
import {
  creditUsageCollapsedIntoPrimaryAtom,
  dictateCollapsedIntoPrimaryAtom,
  discoveryCallCollapsedIntoPrimaryAtom,
  discoveryCallPrimaryEntryAtom,
  helpCollapsedIntoPrimaryAtom,
  helpDropdownOpenAtom,
  notificationsCollapsedIntoPrimaryAtom,
  notificationsPopoverOpenAtom,
} from '@/atoms/sideNavAtoms';
import { type FooterMoreRowDescriptor } from '@/components/layout/Navigation/sidebar/footer-rail/footer-rail-context';
import { IS_DICTATION_LOGS_VISIBLE } from '@/features/flags/legacy-flags';
import { useAtomValue, useSetAtom } from 'jotai';
import { BellIcon, CreditCard, HelpCircleIcon, PhoneIcon, Speech } from 'lucide-react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useIsRouteActive } from './useIsRouteActive';

/**
 * Returns descriptors for footer items that have been collapsed into the
 * primary nav More group (Dictate, Help, Notifications, Credit usage).
 * These are appended at the end of `usePrimaryNavMoreDescriptors`.
 */
export function useCollapsedFooterDescriptors(): readonly FooterMoreRowDescriptor[] {
  const intl = useIntl();
  const isRouteActive = useIsRouteActive();

  const isDiscoveryCallCollapsedIntoPrimary = useAtomValue(
    discoveryCallCollapsedIntoPrimaryAtom
  );
  const discoveryCallEntry = useAtomValue(discoveryCallPrimaryEntryAtom);
  const isDictateCollapsedIntoPrimary = useAtomValue(dictateCollapsedIntoPrimaryAtom);
  const isDictationEnabled = IS_DICTATION_LOGS_VISIBLE();
  const isHelpCollapsedIntoPrimary = useAtomValue(helpCollapsedIntoPrimaryAtom);
  const isNotificationsCollapsedIntoPrimary = useAtomValue(
    notificationsCollapsedIntoPrimaryAtom
  );
  const isCreditUsageCollapsedIntoPrimary = useAtomValue(
    creditUsageCollapsedIntoPrimaryAtom
  );
  const setNotificationsOpen = useSetAtom(notificationsPopoverOpenAtom);
  const setHelpDropdownOpen = useSetAtom(helpDropdownOpenAtom);
  const { open: openSettingsModal } = useSettingsModal();

  return useMemo<readonly FooterMoreRowDescriptor[]>(() => {
    const items: FooterMoreRowDescriptor[] = [];

    // Cascade position 1: Discovery Call (or Live Demo) before Dictate.
    if (isDiscoveryCallCollapsedIntoPrimary && discoveryCallEntry) {
      items.push({
        id: 'discovery',
        label: discoveryCallEntry.label,
        icon: <PhoneIcon />,
        testId: discoveryCallEntry.testId,
        href: discoveryCallEntry.href,
        external: true,
        onSelect: discoveryCallEntry.onClick,
      });
    }

    if (isDictateCollapsedIntoPrimary && isDictationEnabled) {
      items.push({
        id: 'dictate',
        label: intl.formatMessage({
          defaultMessage: 'Dictate',
          id: '0jFAdH4uFc',
          description: 'Sidebar nav label and tooltip for the dictation history page',
        }),
        icon: <Speech />,
        href: '/dictation-logs',
        testId: 'sidebar-footer-dictation-logs',
        isActive: isRouteActive('/dictation-logs'),
      });
    }

    if (isHelpCollapsedIntoPrimary) {
      items.push({
        id: 'help',
        label: intl.formatMessage({ defaultMessage: 'Help', id: 'SENRqussKY' }),
        icon: <HelpCircleIcon />,
        testId: 'sidebar-footer-help',
        isActive: false,
        onSelect: () => setHelpDropdownOpen(true),
      });
    }

    if (isNotificationsCollapsedIntoPrimary) {
      items.push({
        id: 'notifications',
        label: intl.formatMessage({ defaultMessage: 'Notifications', id: 'NAidKbB0vi' }),
        icon: <BellIcon />,
        testId: 'sidebar-footer-notifications',
        isActive: false,
        onSelect: () => setNotificationsOpen(true),
      });
    }

    if (isCreditUsageCollapsedIntoPrimary) {
      items.push({
        id: 'creditUsage',
        label: intl.formatMessage({ defaultMessage: 'Usage', id: 'wbsq7OdxnV' }),
        icon: <CreditCard />,
        testId: 'sidebar-footer-credit-usage',
        isActive: false,
        onSelect: () => openSettingsModal('/settings/billing'),
      });
    }

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDiscoveryCallCollapsedIntoPrimary,
    discoveryCallEntry,
    isDictateCollapsedIntoPrimary,
    isDictationEnabled,
    isHelpCollapsedIntoPrimary,
    isNotificationsCollapsedIntoPrimary,
    isCreditUsageCollapsedIntoPrimary,
    intl,
    isRouteActive,
    setNotificationsOpen,
    setHelpDropdownOpen,
    openSettingsModal,
  ]);
}
